import { useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { useForm, type SubmitHandler } from "react-hook-form";
import { Container, Row, Col, Toast, ToastContainer } from "react-bootstrap";
import { LuCheck, LuX } from "react-icons/lu";

import Card from "@/components/shared/Card";
import LoaderButton from "@/components/shared/LoaderButton";
import { useCoreData } from "@/queries";
import type { ReportingCalendarModel } from "@/models";
import {
  stateLevelDataTypes,
  stateLevelViewTypes,
  useCreateStateLevelExtract,
  type CreateStateLevelExtractRequest,
} from "@/queries/stateLevelExtractsApi";

const getEnrollmentMonthOptions = (
  reportingCalendar: ReportingCalendarModel[]
) => {
  const now = new Date();
  const today = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")}`;
  return reportingCalendar
    .filter(
      (c) =>
        c.CAPSDACsnpshtCode.startsWith("MNTH") &&
        c.EfctvStartDate.slice(0, 10) <= today
    )
    .sort((a, b) => b.EfctvStartDate.localeCompare(a.EfctvStartDate))
    .map((c) => {
      const [year, month] = c.EfctvStartDate.slice(0, 10).split("-").map(Number);
      return {
        Value: c.CAPSDACsnpshtCode,
        Text: new Date(year, month - 1, 1).toLocaleDateString("en-US", {
          month: "long",
          year: "numeric",
        }),
      };
    });
};

export default function RequestStateLevelExtractCard({
  className,
}: {
  className?: string;
}) {
  const createMutation = useCreateStateLevelExtract();
  const coreData = useCoreData();
  const monthOptions = useMemo(
    () => getEnrollmentMonthOptions(coreData?.reportingCalendar ?? []),
    [coreData]
  );
  const [toast, setToast] = useState<{
    title: string;
    message: string;
    variant: "success" | "error";
  } | null>(null);

  const {
    handleSubmit,
    register,
    reset,
    formState: { isValid, isSubmitting },
  } = useForm<CreateStateLevelExtractRequest>({
    mode: "onChange",
    defaultValues: {
      ExtractType: "",
      ExtractFileName: "",
      ResultsEmailDestination: "",
      StateEnrollmentParameters: {
        ExtractDataType: "",
        EnrollmentMonth: "",
      },
    },
  });

  const onSubmit: SubmitHandler<CreateStateLevelExtractRequest> = async (
    data
  ) => {
    try {
      await createMutation.mutateAsync(data);
      setToast({
        title: "Success",
        message: "Export request submitted successfully.",
        variant: "success",
      });
      reset();
    } catch {
      setToast({
        title: "Error",
        message: "Failed to submit export request. Please try again.",
        variant: "error",
      });
    }
  };

  const toastPrefix = toast?.variant === "success" ? "toastSuccess" : "toastError";

  return (
    <>
      <Card className={className}>
        <div className="fs-5 fw-bold mb-3">Request Export</div>
        <form onSubmit={(e) => void handleSubmit(onSubmit)(e)}>
          <Container
            className="ps-3 pe-3 request-container"
            style={{ borderTop: "none", borderRadius: 0 }}
          >
            <Row className="form-group row">
              <Col lg={3} className="text-lg-end">
                <label htmlFor="ExtractType" className="mb-0">
                  View Type
                </label>
              </Col>
              <Col lg={7}>
                <select
                  id="ExtractType"
                  className="form-select"
                  disabled={isSubmitting}
                  aria-required="true"
                  {...register("ExtractType", { required: true })}
                >
                  <option value="">Select option</option>
                  {stateLevelViewTypes.map((type) => (
                    <option key={type.Value} value={type.Value}>
                      {type.Text}
                    </option>
                  ))}
                </select>
              </Col>
            </Row>
            <Row className="form-group row">
              <Col lg={3} className="text-lg-end">
                <label htmlFor="EnrollmentMonth" className="mb-0">
                  Enrollment Month
                </label>
              </Col>
              <Col lg={7}>
                <select
                  id="EnrollmentMonth"
                  className="form-select"
                  disabled={isSubmitting}
                  aria-required="true"
                  {...register("StateEnrollmentParameters.EnrollmentMonth", {
                    required: true,
                  })}
                >
                  <option value="">Select option</option>
                  {monthOptions.map((month) => (
                    <option key={month.Value} value={month.Value}>
                      {month.Text}
                    </option>
                  ))}
                </select>
              </Col>
            </Row>
            <Row className="form-group row">
              <Col lg={3} className="text-lg-end">
                <label htmlFor="ExtractDataType" className="mb-0">
                  Data Type
                </label>
              </Col>
              <Col lg={7}>
                <select
                  id="ExtractDataType"
                  className="form-select"
                  disabled={isSubmitting}
                  aria-required="true"
                  {...register("StateEnrollmentParameters.ExtractDataType", {
                    required: true,
                  })}
                >
                  <option value="">Select option</option>
                  {stateLevelDataTypes.map((type) => (
                    <option key={type} value={type}>
                      {type}
                    </option>
                  ))}
                </select>
              </Col>
            </Row>
            <Row className="form-group row">
              <Col lg={3} className="text-lg-end">
                <label htmlFor="ExtractFileName" className="mb-0">
                  (Optional) Filename
                </label>
              </Col>
              <Col lg={7}>
                <input
                  id="ExtractFileName"
                  className="form-control"
                  type="text"
                  maxLength={50}
                  disabled={isSubmitting}
                  {...register("ExtractFileName")}
                />
              </Col>
            </Row>
            <Row className="form-group row">
              <Col lg={3} className="text-lg-end">
                <label htmlFor="ResultsEmailDestination" className="mb-0">
                  (Optional) Email Address to Notify When Extract Is Ready
                </label>
              </Col>
              <Col lg={7}>
                <input
                  id="ResultsEmailDestination"
                  className="form-control"
                  type="email"
                  maxLength={50}
                  disabled={isSubmitting}
                  {...register("ResultsEmailDestination")}
                />
              </Col>
            </Row>
          </Container>
          <div className="text-right">
            <LoaderButton
              variant="primary"
              type="submit"
              disabled={!isValid || isSubmitting}
              loading={isSubmitting}
              loadingText="Submitting..."
            >
              Request Export
            </LoaderButton>
          </div>
        </form>
      </Card>

      {toast &&
        createPortal(
          <ToastContainer position="top-end" className="p-3" style={{ zIndex: 2000 }}>
            <Toast
              show
              onClose={() => setToast(null)}
              delay={3000}
              autohide
              className={`${toastPrefix}Card`}
            >
              <div className={`${toastPrefix}Inner`}>
                <div className={`${toastPrefix}Strip`} />
                <div className={`${toastPrefix}Main`}>
                  <div className={`${toastPrefix}Icon`}>
                    {toast.variant === "success" ? <LuCheck size={20} /> : <LuX size={20} />}
                  </div>
                  <div className={`${toastPrefix}Text`}>
                    <div className={`${toastPrefix}Title`}>{toast.title}</div>
                    <div className={`${toastPrefix}Message`}>{toast.message}</div>
                  </div>
                  <button
                    type="button"
                    className="btn-close ms-auto"
                    aria-label="Close"
                    onClick={() => setToast(null)}
                  />
                </div>
              </div>
            </Toast>
          </ToastContainer>,
          document.body
        )}
    </>
  );
}
